import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import ProfileSettings from '../settings/ProfileSettings';
import AccountSettings from '../settings/AccountSettings';
import NotificationSettings from '../settings/NotificationSettings';
import SecuritySettings from '../settings/SecuritySettings';
import InviteUsers from '../settings/InviteUsers';
import IntegrationSettings from '../settings/IntegrationSettings';
import AppearanceSettings from '../settings/AppearanceSettings';
import ProjectConfiguration from '../settings/ProjectConfiguration';
import AgentInstallation from '../settings/AgentInstallation';
import { Breadcrumbs, PageScaffold } from '../ui';
import '../../styles/settings.css';

const SCOPES = [
  {
    id: 'account',
    label: 'My Account',
    items: [
      { id: 'profile', label: 'Profile', component: ProfileSettings },
      { id: 'account', label: 'Preferences', component: AccountSettings },
      { id: 'notifications', label: 'Notifications', component: NotificationSettings },
      { id: 'security', label: 'Security', component: SecuritySettings },
      { id: 'appearance', label: 'Appearance', component: AppearanceSettings },
    ],
  },
  {
    id: 'workspace',
    label: 'Workspace',
    items: [
      { id: 'invite', label: 'Members', component: InviteUsers },
      { id: 'integrations', label: 'Integrations', component: IntegrationSettings },
    ],
  },
  {
    id: 'project',
    label: 'Project',
    items: [
      { id: 'project-config', label: 'Configuration', component: ProjectConfiguration },
    ],
  },
  {
    id: 'automation',
    label: 'Automation',
    items: [
      { id: 'agent-installation', label: 'Agents', component: AgentInstallation },
    ],
  },
];

/** Titles match sidebar labels so we don't repeat "Settings" / section name twice. */
const SECTION_META = {
  profile: { title: 'Profile', description: 'Update your personal information and avatar.' },
  account: { title: 'Preferences', description: 'Account preferences and defaults.' },
  notifications: { title: 'Notifications', description: 'Choose how and when you are notified.' },
  security: { title: 'Security', description: 'Password, sessions and account protection.' },
  appearance: { title: 'Appearance', description: 'Theme and display preferences for Robitest.' },
  invite: { title: 'Members', description: 'Invite teammates and manage workspace access.' },
  integrations: { title: 'Integrations', description: 'Connect Jira, CI/CD and other tools.' },
  'project-config': { title: 'Configuration', description: 'Project-level automation and environment defaults.' },
  'agent-installation': {
    title: 'Agents',
    description: 'Download, install, and verify the local Robitest agent for recording and execution.',
  },
};

/**
 * Scoped Settings — My Account / Workspace / Project / Automation
 * Reuses existing settings section components inside the canonical AppShell.
 */
const SettingsScoped = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial =
    searchParams.get('section') ||
    location.state?.activeSection ||
    (location.state?.autoOpenIntegration ? 'integrations' : 'profile');

  const [activeSection, setActiveSection] = useState(initial);

  useEffect(() => {
    if (location.state?.autoOpenIntegration) {
      setActiveSection('integrations');
    }
    if (location.state?.activeSection) {
      setActiveSection(location.state.activeSection);
    }
  }, [location.state]);

  useEffect(() => {
    const fromQuery = searchParams.get('section');
    if (fromQuery && fromQuery !== activeSection) {
      setActiveSection(fromQuery);
    }
  }, [searchParams, activeSection]);

  const selectSection = (id) => {
    setActiveSection(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('section', id);
        return next;
      },
      { replace: true }
    );
  };

  const ActiveComponent = useMemo(() => {
    for (const scope of SCOPES) {
      const item = scope.items.find((i) => i.id === activeSection);
      if (item) return item.component;
    }
    return ProfileSettings;
  }, [activeSection]);

  const meta = SECTION_META[activeSection] || {
    title: 'Settings',
    description: 'Manage your account, workspace, project and automation preferences.',
  };

  return (
    <PageScaffold
      hideHeader
      breadcrumbs={
        <Breadcrumbs
          items={[
            { label: 'System' },
            { label: 'Settings' },
            { label: meta.title },
          ]}
        />
      }
      embedLegacy
    >
      <div className="rt-hub-layout rt-settings-layout">
        <nav className="rt-hub-nav" aria-label="Settings scopes">
          {SCOPES.map((scope) => (
            <div key={scope.id} className="rt-settings-nav-group">
              <div className="rt2-sidebar__section-title rt-settings-nav-group__label">
                {scope.label}
              </div>
              {scope.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`rt-hub-nav__item${activeSection === item.id ? ' rt-hub-nav__item--active' : ''}`}
                  onClick={() => selectSection(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="rt-hub-content">
          <div className="rt-settings-panel rt-legacy-embed">
            <div className="settings-container" style={{ padding: 0 }}>
              <div id={`${activeSection}-section`}>
                <ActiveComponent />
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageScaffold>
  );
};

export default SettingsScoped;
